import { NAMED_ABSENCE_REASONS, type AbsenceReason } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { ABSENCE_REASON_LABELS } from '../lib/format';
import { invalidateSports } from '../lib/sports-termine';
import { formatDay, today, type RosterRow, type TeamAbsence } from '../lib/sports';
import { useConfirm } from './ConfirmDialog';
import { Badge, Button, Card, CardBody, EmptyState, Field, Input, LoadingBlock, SectionTitle, Select, useToast } from './ui';

interface Entwurf {
  id: string | null;
  personId: string;
  reason: AbsenceReason;
  fromDate: string;
  toDate: string;
  note: string;
}

const LEER = (): Entwurf => ({ id: null, personId: '', reason: 'VACATION', fromDate: today(), toDate: today(7), note: '' });

/**
 * Längere Abwesenheiten des Kaders.
 *
 * Für jeden Termin im Zeitraum gilt die Person als abgemeldet - bis sie für
 * einen einzelnen Termin selbst zusagt. Laufende und kommende Einträge
 * stehen oben, vergangene darunter.
 */
export function AbsencesCard({ teamId, seasonId, roster }: { teamId: string; seasonId: string; roster: RosterRow[] }): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [vergangeneZeigen, setVergangeneZeigen] = useState(false);
  const queryKey = ['sports-absences', teamId, seasonId];

  const absences = useQuery({
    queryKey,
    queryFn: () => api.get<{ canManage: boolean; canReadReasons: boolean; absences: TeamAbsence[] }>(`/api/sports/teams/${teamId}/absences`, { seasonId }),
    enabled: Boolean(teamId && seasonId),
  });

  const nachAenderung = async (meldung: string): Promise<void> => {
    await qc.invalidateQueries({ queryKey });
    await invalidateSports(qc);
    toast.show(meldung);
  };
  const fehler = (fallback: string) => (error: unknown) =>
    toast.show(error instanceof ApiError ? error.message : fallback, 'error');

  const speichern = useMutation({
    mutationFn: (arg: { wert: Entwurf; cancelResponses: boolean }) => {
      const { wert, cancelResponses } = arg;
      const body = { personId: wert.personId, reason: wert.reason, fromDate: wert.fromDate, toDate: wert.toDate, note: wert.note.trim() || null, cancelResponses };
      return wert.id
        ? api.put<{ cancelledResponses?: number }>(`/api/sports/teams/${teamId}/absences/${wert.id}?seasonId=${seasonId}`, body)
        : api.post<{ cancelledResponses?: number }>(`/api/sports/teams/${teamId}/absences?seasonId=${seasonId}`, body);
    },
    onSuccess: async (result) => {
      setEntwurf(null);
      const abgesagt = result?.cancelledResponses ?? 0;
      await nachAenderung(
        abgesagt > 0
          ? `Abwesenheit gespeichert und ${abgesagt} ${abgesagt === 1 ? 'Zusage' : 'Zusagen'} abgesagt`
          : 'Abwesenheit gespeichert',
      );
    },
    onError: fehler('Abwesenheit konnte nicht gespeichert werden'),
  });

  const loeschen = useMutation({
    mutationFn: (id: string) => api.del(`/api/sports/teams/${teamId}/absences/${id}?seasonId=${seasonId}`),
    onSuccess: () => nachAenderung('Abwesenheit gelöscht'),
    onError: fehler('Abwesenheit konnte nicht gelöscht werden'),
  });

  /** Fragt vor dem Speichern, ob bereits gegebene Zusagen im Zeitraum zu Absagen werden sollen. */
  const speichernMitPruefung = async (wert: Entwurf): Promise<void> => {
    let zusagen = 0;
    try {
      const pruefung = await api.get<{ responses: number }>(`/api/sports/teams/${teamId}/absences/conflicts`, {
        seasonId,
        personId: wert.personId,
        fromDate: wert.fromDate,
        toDate: wert.toDate,
      });
      zusagen = pruefung.responses ?? 0;
    } catch {
      // Die Prüfung ist nur eine Hilfe: Ohne sie wird wie bisher gespeichert, Zusagen bleiben.
    }
    let cancelResponses = false;
    if (zusagen > 0) {
      const person = roster.find((r) => r.person_id === wert.personId);
      const name = person ? `${person.first_name} ${person.last_name}` : 'Die Person';
      cancelResponses = await confirm({
        title: 'Zusagen absagen?',
        message: `${name} hat für ${zusagen} ${zusagen === 1 ? 'Termin' : 'Termine'} im Zeitraum schon zugesagt. Diese Zusagen in Absagen umwandeln?`,
        confirmLabel: 'Ja, absagen',
        cancelLabel: 'Nein, Zusagen lassen',
      });
    }
    speichern.mutate({ wert, cancelResponses });
  };

  const loeschenFragen = async (a: TeamAbsence): Promise<void> => {
    const ok = await confirm({
      title: 'Abwesenheit löschen?',
      message: `Die Abwesenheit von ${a.first_name} ${a.last_name} (${formatDay(a.from_date)} – ${formatDay(a.to_date)}) wird gelöscht. Für die Termine in diesem Zeitraum gilt ${a.first_name} nicht mehr als abgemeldet.`,
      confirmLabel: 'Ja, löschen',
      tone: 'danger',
    });
    if (ok) loeschen.mutate(a.id);
  };

  const heute = today();
  const alle = absences.data?.absences ?? [];
  const aktuell = alle.filter((a) => a.to_date >= heute).sort((a, b) => a.from_date.localeCompare(b.from_date));
  const vergangen = alle.filter((a) => a.to_date < heute);
  const canManage = absences.data?.canManage ?? false;

  const zeile = (a: TeamAbsence): JSX.Element => {
    const laeuft = a.from_date <= heute && a.to_date >= heute;
    return (
      <li key={a.id} className="flex flex-wrap items-start justify-between gap-2 py-2 text-sm">
        <div className="min-w-0">
          <p className="font-medium">
            {a.first_name} {a.last_name}
            {laeuft && <Badge tone="warning" className="ml-2">läuft</Badge>}
          </p>
          <p className="text-slate-600">
            {formatDay(a.from_date)} – {formatDay(a.to_date)}
            {a.reason ? ` · ${ABSENCE_REASON_LABELS[a.reason] ?? a.reason}` : ''}
            {a.note ? ` · ${a.note}` : ''}
          </p>
          {a.source === 'PERSONAL_LINK' && <p className="text-xs text-slate-500">selbst eingetragen</p>}
        </div>
        {canManage && (
          <div className="flex gap-3">
            {a.reason !== null && (
              <button
                className="text-brand-700 hover:underline"
                onClick={() => setEntwurf({ id: a.id, personId: a.person_id, reason: a.reason as AbsenceReason, fromDate: a.from_date, toDate: a.to_date, note: a.note ?? '' })}
              >
                Bearbeiten
              </button>
            )}
            <button className="text-rose-700 hover:underline" onClick={() => void loeschenFragen(a)}>Löschen</button>
          </div>
        )}
      </li>
    );
  };

  if (absences.error) return <></>;

  return (
    <Card>
      <CardBody className="space-y-3">
        {confirmDialog}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <SectionTitle title="Abwesenheiten" subtitle="Urlaub, Verletzung & Co. – gilt als Absage für alle Termine im Zeitraum" />
          {canManage && !entwurf && roster.length > 0 && (
            <Button size="sm" onClick={() => setEntwurf(LEER())}>Abwesenheit eintragen</Button>
          )}
        </div>

        {entwurf && (
          <div className="space-y-3 rounded-xl border border-slate-200 p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Person" required>
                {(id) => (
                  <Select id={id} value={entwurf.personId} disabled={entwurf.id !== null} onChange={(e) => setEntwurf({ ...entwurf, personId: e.target.value })}>
                    <option value="">Person wählen …</option>
                    {roster.map((r) => <option key={r.person_id} value={r.person_id}>{r.first_name} {r.last_name}</option>)}
                  </Select>
                )}
              </Field>
              <Field label="Grund" required>
                {(id) => (
                  <Select id={id} value={entwurf.reason} onChange={(e) => setEntwurf({ ...entwurf, reason: e.target.value as AbsenceReason })}>
                    {NAMED_ABSENCE_REASONS.map((value) => <option key={value} value={value}>{ABSENCE_REASON_LABELS[value]}</option>)}
                  </Select>
                )}
              </Field>
              <Field label="Von" required>
                {(id) => <Input id={id} type="date" value={entwurf.fromDate} onChange={(e) => setEntwurf({ ...entwurf, fromDate: e.target.value, toDate: e.target.value > entwurf.toDate ? e.target.value : entwurf.toDate })} />}
              </Field>
              <Field label="Bis (ca.)" required hint="Lässt sich jederzeit verlängern oder verkürzen">
                {(id) => <Input id={id} type="date" min={entwurf.fromDate} value={entwurf.toDate} onChange={(e) => setEntwurf({ ...entwurf, toDate: e.target.value })} />}
              </Field>
            </div>
            <Field label="Notiz">
              {(id) => <Input id={id} value={entwurf.note} maxLength={200} onChange={(e) => setEntwurf({ ...entwurf, note: e.target.value })} placeholder="z. B. Kreuzband, Reha" />}
            </Field>
            <div className="flex gap-2">
              <Button
                size="sm"
                loading={speichern.isPending}
                disabled={!entwurf.personId || !entwurf.fromDate || !entwurf.toDate || entwurf.toDate < entwurf.fromDate}
                onClick={() => void speichernMitPruefung(entwurf)}
              >
                Speichern
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setEntwurf(null)}>Abbrechen</Button>
            </div>
          </div>
        )}

        {absences.isLoading && <LoadingBlock />}
        {!absences.isLoading && aktuell.length === 0 && (
          <EmptyState title="Keine laufenden Abwesenheiten" description="Eingetragene Zeiträume erscheinen hier – auch die, die Spieler selbst über ihren Link melden." />
        )}
        {aktuell.length > 0 && <ul className="divide-y divide-slate-100">{aktuell.map(zeile)}</ul>}

        {vergangen.length > 0 && (
          <div>
            <button className="text-sm text-slate-500 hover:underline" onClick={() => setVergangeneZeigen(!vergangeneZeigen)}>
              {vergangeneZeigen ? 'Vergangene ausblenden' : `Vergangene anzeigen (${vergangen.length})`}
            </button>
            {vergangeneZeigen && <ul className="mt-1 divide-y divide-slate-100 opacity-70">{vergangen.map(zeile)}</ul>}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
